import dayjs from "dayjs";
import {
  TGraphData,
  TChartData,
  TDataset,
  TDatasets,
  TCustomProcessFn,
  TTableData,
  TDiseasePestData,
} from "./types";
import { TInformationCard } from "./components/InformationCardRenderer";
import SensorDataService, { SampleItem, TDataItem } from "../../services/sensorData.service";
import { t } from "../../services/locale.service";
import xlsx, { IColumn } from "json-as-xlsx";

export const generateUniqueId = (): string => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 10; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  result += Date.now().toString(36);
  return result;
};

export const fetchAndProcessSensorData = async (args: TFetchSensorDataArguments): Promise<TFetchSensorDataResponse> => {
  const {plotId, startDate, endDate, field, dataType, customProcessFn} = args;
  try {
    const graphData = await SensorDataService.getSensorData({
      plotId,
      startDate,
      endDate,
      field,
      dataType: dataType ? dataType : "daily",
    });

    const { sample, min, max, avg } = customProcessFn
      ? customProcessFn(graphData, startDate, endDate)
      : graphData;

    const sum = graphData.sum ?? 0;

    const processedData = {
      graphData: sample,
      chartData: {
        normalRange: sample,
        min,
        max,
        avg,
        sum,
      },
      data: graphData.sample as TDataItem[]
    };

    return processedData;
  } catch (error) {
    console.error("Error fetching sensor data:", error);
    throw error;
  }
};

export const fetchAndProcessDiseasePestData = async (args: TFetchDiseasePestDataArguments) => {
  const { plotId, diseaseId, startDate, endDate, dataFor } = args;
  try {
    const { res } = await SensorDataService.getDiseasePestData({
      plotId, 
      diseaseId, 
      startDate, 
      endDate,
      dataFor
    });

    return res.map((data: TDiseasePestData) => {
      return {
        [diseaseId]: data.probability,
        timestamp: data.calculatedDate,
        riskLevel: data.riskLevel,
        probability: data.probability
      };
    });

  } catch(error) {
    console.error(error);
  }
};

export const processTableData = (data: Array<TDataItem>, field: string ): { data: TTableData, columns: Array<string> } => {
  const columns = getTableColumns(field);
  if(Boolean(data) && Array.isArray(data)) {
    const tableData = data.map((obj) => {
      if(field === "disease/pest") {
        return [dayjs(obj.timestamp).format("DD MMM YYYY"), Number(obj.probability).toFixed(2), obj.riskLevel];
      } else if (field === "eto") {
        const { date } = obj;
        return [obj[field], dayjs(date).format("DD/MM/YYYY, hh:mm a")];
      } else if (field === "gdd") {
        const { date } = obj;
        return [dayjs(date).format("DD/MM/YYYY, hh:mm a"), obj[field]];
      } else if(field === "lai") {
        const { datetime, cloudCoverPercentage } = obj;
        return [dayjs(datetime).format("DD/MM/YYYY, hh:mm a"), obj[field], cloudCoverPercentage];
      } else if(SensorsWithWetType.includes(field)) {
        const { timestamp } = obj;
        return [dayjs(timestamp).format("DD/MM/YYYY, hh:mm a"), obj["WetType"] ?? Number(obj[field])?.toFixed(2)];
      } else {
        const { min, max, timestamp } = obj;
        return [dayjs(timestamp).format("DD/MM/YYYY, hh:mm a"), min?.toFixed(2), max?.toFixed(2), Number(obj[field])?.toFixed(2)];
      }
    });

    const hasWetType = tableData.length > 0 && (tableData[0][1] === "Wet" || tableData[0][1] === "Dry");
    const columns = getTableColumns(field, hasWetType);

    return {
      data: tableData, 
      columns
    };
  }
  return {
    data: [],
    columns
  };
};


export const generateChartDatasets = (graphData: TGraphData, field: string, timeframe: string = "daily") => {
  const dataset: TDataset = {
    label: t(field),
    data: [],
    backgroundColor: "green",
  };

  const timestampFormat = timeframe !== "day" ? "DD MMM" : "DD MMM, hh a";

  const timestamps = (graphData ?? []).map((data) => {
    if("timestamp" in data) {
      return dayjs(data.timestamp).format(timestampFormat);
    }
    return "";
  }
  );

  (graphData ?? []).forEach((data) => {
    dataset.data.push((data as SampleItem)[field] as number);
  });

  const datasets: TDatasets = [dataset];

  return { datasets, timestamps };
};

/**
 * Charts Inforamation Cards
 */

export const generateInformationCardData = (
  chartData: TChartData,
  field: string,
  unit: string
): Array<TInformationCard> => {
  const hasChartDataMin = Boolean(chartData?.min?.[field]);
  const hasChartDataMax = Boolean(chartData?.max?.[field]);
  const hasChartDataAvg = Boolean(chartData?.avg);

  const informationCards: Array<TInformationCard> = [];

  if (hasChartDataMin) {
    const minValue = chartData.min.min ?? chartData.min[field];
    if (minValue !== undefined) {
      informationCards.push({
        cardTitle: t("lowest"),
        value: Number((minValue as number).toFixed(1)),
        unit,
        information: dayjs(chartData.min.timestamp).format("DD/MM/YYYY, hh:mm a"),
      });
    }
  }

  if (hasChartDataAvg) {
    informationCards.push({
      cardTitle: t("average"),
      value: Number(chartData.avg.toFixed(1)),
      unit,
      information: "*" + t("onAverage"),
    });
  }

  if (hasChartDataMax) {
    const maxValue = chartData.max.max ?? chartData.max[field];
    if (maxValue !== undefined) {
      informationCards.push({
        cardTitle: t("highest"),
        value: Number((maxValue as number).toFixed(1)),
        unit,
        information: dayjs(chartData.max.timestamp).format("DD/MM/YYYY, hh:mm a"),
      });
    }
  }

  return informationCards;
};

const getTableColumns = (field: string, hasWetType: boolean = false) => {
  const MinMaxHeader = [t("Date"), t("Minimum"), t("Maximum"), t("average")];
  const WetTypeHeader = [t("Date"), "WetType"];
  const SensorNameHeader = [t("Date"), t(field)];
  const DiseasePestHeader = [t("Date"), t("probability"), t("RiskLevel")];

  if(field === "disease/pest") {
    return DiseasePestHeader;
  }

  if(SensorsWithWetType.includes(field)) {
    return hasWetType ? WetTypeHeader : SensorNameHeader;
  }
  return MinMaxHeader;
};

export const getDataType = (field: string, startDate: string, endDate: string): "daily" | "hourly" => {
  const diff = dayjs(endDate).diff(startDate, "days");

  if(field === "leafWetness") {
    return diff > 1 ? "daily" : "hourly";
  }

  const dataType = diff < 31 ? "hourly" : "daily";

  return dataType;
};

export const generateDownloadData = (data: Array<TDataItem>) => {
  return data?.map((obj) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {x, y, ...rest} = obj;

    return rest;
  });
};

export const downloadXLSX = (data: TXlsxData, fileName: string = "sensor-data") => {
  if(!data || data.length === 0) {
    alert("No Data!");
    return;
  }
  const columns = generateXlsxColumns(data);
  
  const sheetData = [{
    sheet: "Data",
    columns,
    content: data
  }];

  const settings = {
    fileName,
    extraLength: 3, // A bigger number means that columns will be wider
    writeOptions: {} // Style options from https://github.com/SheetJS/sheetjs#writing-options
  };

  xlsx(sheetData, settings);
};

const generateXlsxColumns = (data: TXlsxData) => {
  return Object.keys(data[0]).map(key => ({label: key, value: key})) as IColumn[];
};


/**
 * ================== TYPES ======================
 */

type TXlsxData = Array<Record<string, string | number | Date | boolean>>;

export type TFetchSensorDataArguments = {
  plotId: string,
  startDate: string,
  endDate: string,
  field: string,
  dataType: "daily" | "hourly",
  customProcessFn?: TCustomProcessFn,
}

export type TFetchSensorDataResponse = { graphData: TGraphData; chartData: TChartData, data?: Array<TDataItem> };

export type TFetchDiseasePestDataArguments = {
  plotId: string;
  startDate: string;
  endDate: string;
  diseaseId: string;
  dataFor: "Disease" | "Pest";
}

/**
 * ================== CONSTANTS ======================
 */

const SensorsWithWetType = ["deltaT", "vpd", "leafWetness", "rainFall"];